import { create } from "zustand";
import {
  IPQuestion,
  IRankingProjectQuestion,
  IRankingEquation,
} from "../models/interface";
import { RankingProjectService } from "../services/rankingProject.service";
import { validateEquationContent } from "../orgRankingTools/utils/equationValidation";

interface IPQuestionTree extends IPQuestion {
  children?: IPQuestionTree[];
}

interface IEquationObj {
  name: string;
  equation: string;
  result: 0 | 1 | null;
  // Sourced from getRankingEquations (tbl_ranking_project.ranks). Used by CalculateEquation's
  // operatorIMPORT to block a lower rank (higher display_order) importing a higher rank (lower
  // display_order) — see calculateEquation.ts's selfDisplayOrder.
  display_order?: number | null;
}

// Fired by the equation textarea's onItemSelected (RankingToolsEditor) when a user picks a
// question/response variable from the @/# autocomplete — consumed by QuestionListContainer to
// scroll to and briefly highlight that variable's row. `nonce` (not just `variable`) so selecting
// the SAME variable twice in a row still produces a new object reference and re-fires the
// listener's effect — a plain string wouldn't change and the second selection would be a no-op.
interface IScrollToVariableTarget {
  variable: string;
  nonce: number;
}

export interface RankingToolState {
  questionSnapshot: null | IRankingProjectQuestion[];
  questionSnapshotList: null | IPQuestion[];
  scrollToVariableTarget: IScrollToVariableTarget | null;
  // Variable name(s) currently under the equation editor's cursor and/or mouse hover (see
  // EquationCodeMirrorEditor's onActiveVariablesChange) — drives a PERSISTENT background
  // highlight on the matching Question Snapshot row(s), unlike scrollToVariableTarget above
  // which is a one-shot "scroll to this on autocomplete pick" signal. Empty Set when neither the
  // cursor nor the mouse is currently over a known variable.
  activeVariables: Set<string>;
  lastSavedQuestionSnapshot: null | IRankingProjectQuestion[];
  rankingTypeId: number | null;
  isLoadingQuestionSnapshot: boolean;
  isSavingQuestionSnapshot: boolean;
  isQuestionSnapshotDirty: boolean;
  currentRankTab: string;
  ranks: string[];
  // Full rank metadata (name, display_order, icon) as returned by getRankingEquations, ordered by
  // display_order — kept alongside the simpler `ranks` name list so a future IMPORT tier-ordering
  // enforcement fix has display_order available without a second fetch. Null until fetched once.
  rankDetails: IRankingEquation[] | null;
  isLoadingRankingEquations: boolean;
  allEquations: { [key: string]: IEquationObj };
  savedEquations: { [key: string]: string };
  isEquationsDirty: boolean;
  // Names of ranks whose equations are currently being saved — a SET, not a single value,
  // because two saves can genuinely overlap (user saves Rank A, switches tabs before it
  // resolves, saves Rank B too). A single "current saving rank" slot would get overwritten by
  // the second save and falsely clear the first save's loading/disabled indicator while its
  // request was still in flight.
  savingEquationRanks: Set<string>;
  currentEquation: IEquationObj | undefined;

  getQuestionSnapshot: (
    project_slug: string,
    ranking_project_id: number,
  ) => Promise<void>;
  getRankingEquations: (
    project_slug: string,
    ranking_project_id: number,
  ) => Promise<void>;
  refreshQuestionSnapshot: (
    project_slug: string,
    ranking_project_id: number,
  ) => Promise<void>;
  saveQuestionSnapshot: (
    project_slug: string,
    ranking_project_id: number,
  ) => Promise<void>;
  updateResponseScore: (
    questionId: number,
    responseId: number,
    score: number | null,
  ) => void;
  clearResponseScores: () => void;
  setCurrentTab: (value: string) => void;
  setEquationText: (equationText: string) => void;
  setScrollToVariable: (variable: string) => void;
  setActiveVariables: (variables: Set<string>) => void;
  saveRankingEquation: (
    project_slug: string,
    ranking_project_id: number,
  ) => Promise<void>;
  clearEquation: () => void;
}

const mergeScoresFromLastSaved = (
  freshQuestions: IRankingProjectQuestion[],
  lastSaved: IRankingProjectQuestion[] | null,
): IRankingProjectQuestion[] => {
  if (!lastSaved) {
    return freshQuestions.map((question) => ({
      ...question,
      responses: (question.responses ?? []).map((response) => ({
        ...response,
        score: 0,
      })),
    }));
  }
  const savedScoreByVariable = new Map<string, number | null>();
  lastSaved.forEach((question) => {
    (question.responses ?? []).forEach((response) => {
      savedScoreByVariable.set(response.variable, response.score);
    });
  });
  return freshQuestions.map((question) => ({
    ...question,
    responses: (question.responses ?? []).map((response) => ({
      ...response,
      score: savedScoreByVariable.has(response.variable)
        ? (savedScoreByVariable.get(response.variable) ?? 0)
        : 0,
    })),
  }));
};

const fetchAndSetQuestionSnapshot = async (
  set: (partial: Partial<RankingToolState>) => void,
  get: () => RankingToolState,
  project_slug: string,
  ranking_project_id: number,
  refresh: boolean,
) => {
  set({ isLoadingQuestionSnapshot: true });
  try {
    const { data } = await new RankingProjectService().getQuestionSnapshot(
      project_slug,
      ranking_project_id,
      refresh,
    );

    const _questionSnapshot =
      refresh && data.data.ranking_type_id === 2
        ? mergeScoresFromLastSaved(
            data.data.question_snapshot ?? [],
            get().lastSavedQuestionSnapshot,
          )
        : (data.data.question_snapshot ?? []);
    const _questionSnapshotList = questionTreeToFlattenQuestionList(
      makeQuestionSnapshotTree(_questionSnapshot, 0),
    );
    set({
      questionSnapshotList: _questionSnapshotList,
      questionSnapshot: _questionSnapshot,
      rankingTypeId: data.data.ranking_type_id ?? null,
      isQuestionSnapshotDirty: !data.data.is_saved_snapshot,
      ...(data.data.is_saved_snapshot
        ? { lastSavedQuestionSnapshot: _questionSnapshot }
        : {}),
    });
  } catch (error) {
    console.log(error);
  } finally {
    set({ isLoadingQuestionSnapshot: false });
  }
};

const useStore = create<RankingToolState>((set, get) => ({
  questionSnapshot: null,
  questionSnapshotList: null,
  scrollToVariableTarget: null,
  activeVariables: new Set(),
  lastSavedQuestionSnapshot: null,
  rankingTypeId: null,
  isLoadingQuestionSnapshot: false,
  isSavingQuestionSnapshot: false,
  isQuestionSnapshotDirty: true,
  currentRankTab: "silver",
  ranks: ["silver", "gold", "platinum"],
  rankDetails: null,
  isLoadingRankingEquations: false,
  allEquations: {},
  savedEquations: {},
  isEquationsDirty: false,
  savingEquationRanks: new Set(),
  currentEquation: undefined,

  getRankingEquations: async (
    project_slug: string,
    ranking_project_id: number,
  ) => {
    set({ isLoadingRankingEquations: true });
    try {
      const { data } = await new RankingProjectService().getRankingEquations(
        project_slug,
        ranking_project_id,
      );
      const rankingEquations = data.data.ranking_equations ?? [];
      if (!rankingEquations.length) {
        return;
      }
      const _allEquations: { [key: string]: IEquationObj } = {};
      const _savedEquations: { [key: string]: string } = {};
      rankingEquations.forEach((eachRank) => {
        const equationText = eachRank.equation ?? "";
        _allEquations[eachRank.rank_name] = {
          name: eachRank.rank_name,
          equation: equationText,
          result: null,
          display_order: eachRank.display_order,
        };
        _savedEquations[eachRank.rank_name] = equationText;
      });
      const _ranks = rankingEquations.map((eachRank) => eachRank.rank_name);
      const _currentRankTab = _ranks.includes(get().currentRankTab)
        ? get().currentRankTab
        : _ranks[0];
      set({
        ranks: _ranks,
        rankDetails: rankingEquations,
        allEquations: _allEquations,
        savedEquations: _savedEquations,
        isEquationsDirty: false,
        currentRankTab: _currentRankTab,
        currentEquation: _allEquations[_currentRankTab],
      });
    } catch (error) {
      console.log(error);
    } finally {
      set({ isLoadingRankingEquations: false });
    }
  },

  getQuestionSnapshot: async (
    project_slug: string,
    ranking_project_id: number,
  ) => {
    await fetchAndSetQuestionSnapshot(
      set,
      get,
      project_slug,
      ranking_project_id,
      false,
    );
  },

  refreshQuestionSnapshot: async (
    project_slug: string,
    ranking_project_id: number,
  ) => {
    await fetchAndSetQuestionSnapshot(
      set,
      get,
      project_slug,
      ranking_project_id,
      true,
    );
  },

  saveQuestionSnapshot: async (
    project_slug: string,
    ranking_project_id: number,
  ) => {
    if (!get().isQuestionSnapshotDirty) {
      return;
    }
    set({ isSavingQuestionSnapshot: true });
    try {
      await new RankingProjectService().saveQuestionSnapshot(
        project_slug,
        ranking_project_id,
        get().questionSnapshot ?? [],
      );
      set({
        isQuestionSnapshotDirty: false,
        lastSavedQuestionSnapshot: get().questionSnapshot,
      });
    } finally {
      set({ isSavingQuestionSnapshot: false });
    }
  },

  saveRankingEquation: async (
    project_slug: string,
    ranking_project_id: number,
  ) => {
    const rank = get().currentRankTab;
    const equationText = get().allEquations[rank]?.equation ?? "";
    const rankDisplayOrderByName = new Map(
      Object.values(get().allEquations).map((eq) => [
        eq.name,
        eq.display_order,
      ]),
    );
    const contentErrors = validateEquationContent(
      equationText,
      get().questionSnapshotList ?? [],
      rankDisplayOrderByName,
      get().allEquations[rank]?.display_order,
    );
    if (contentErrors.length) {
      throw new Error(`${rank}: ${contentErrors.join(" ")}`);
    }
    set({ savingEquationRanks: new Set(get().savingEquationRanks).add(rank) });
    try {
      // An equation references variables that live in the Question Snapshot — if it's never
      // been persisted yet (first time opening this Ranking Project, is_saved_snapshot: false),
      // persist it now too, so the data this equation depends on actually exists in the DB.
      // No-ops if it's already saved (saveQuestionSnapshot self-guards on isQuestionSnapshotDirty).
      await get().saveQuestionSnapshot(project_slug, ranking_project_id);

      await new RankingProjectService().saveRankingEquation(
        project_slug,
        ranking_project_id,
        rank,
        equationText,
      );
      const _allEquations = get().allEquations;
      const _savedEquations = { ...get().savedEquations, [rank]: equationText };
      const _isEquationsDirty = Object.keys(_allEquations).some(
        (rankName) =>
          (_allEquations[rankName].equation ?? "") !==
          (_savedEquations[rankName] ?? ""),
      );
      set({
        savedEquations: _savedEquations,
        isEquationsDirty: _isEquationsDirty,
      });
    } finally {
      const _nextSavingRanks = new Set(get().savingEquationRanks);
      _nextSavingRanks.delete(rank);
      set({ savingEquationRanks: _nextSavingRanks });
    }
  },

  clearEquation: () => {
    const rank = get().currentRankTab;
    const savedText = get().savedEquations[rank] ?? "";
    const _tmpAllEquations = get().allEquations;
    _tmpAllEquations[rank] = {
      ..._tmpAllEquations[rank],
      name: rank,
      equation: savedText,
      result: null,
    };
    const _savedEquations = get().savedEquations;
    const _isEquationsDirty = Object.keys(_tmpAllEquations).some(
      (rankName) =>
        (_tmpAllEquations[rankName].equation ?? "") !==
        (_savedEquations[rankName] ?? ""),
    );
    set({
      allEquations: _tmpAllEquations,
      isEquationsDirty: _isEquationsDirty,
      currentEquation: _tmpAllEquations[rank],
    });
  },

  updateResponseScore: (
    questionId: number,
    responseId: number,
    score: number | null,
  ) => {
    set((state) => ({
      questionSnapshot: (state.questionSnapshot ?? []).map((question) =>
        question.question_id === questionId
          ? {
              ...question,
              responses: (question.responses ?? []).map((response) =>
                response.id === responseId ? { ...response, score } : response,
              ),
            }
          : question,
      ),
      questionSnapshotList: (state.questionSnapshotList ?? []).map(
        (question) =>
          question.id === questionId
            ? {
                ...question,
                responses: question.responses.map((response) =>
                  response.id === responseId
                    ? { ...response, score }
                    : response,
                ),
              }
            : question,
      ),
      isQuestionSnapshotDirty: true,
    }));
  },

  clearResponseScores: () => {
    const _questionSnapshot = mergeScoresFromLastSaved(
      get().questionSnapshot ?? [],
      get().lastSavedQuestionSnapshot,
    );
    set({
      questionSnapshot: _questionSnapshot,
      questionSnapshotList: questionTreeToFlattenQuestionList(
        makeQuestionSnapshotTree(_questionSnapshot, 0),
      ),
      isQuestionSnapshotDirty: !get().lastSavedQuestionSnapshot,
    });
  },

  setCurrentTab: (value) => {
    set((state) => ({
      currentRankTab: value,
      currentEquation: state.allEquations[value] ?? undefined,
    }));
  },

  setScrollToVariable: (variable) => {
    set({ scrollToVariableTarget: { variable, nonce: Date.now() } });
  },

  setActiveVariables: (variables) => {
    set({ activeVariables: variables });
  },

  setEquationText: (equationText) => {
    const rank = get().currentRankTab;
    const _tmpAllEquations = get().allEquations;
    if (!_tmpAllEquations[rank]) {
      _tmpAllEquations[rank] = {
        name: rank,
        equation: equationText,
        result: null,
      };
    } else {
      _tmpAllEquations[rank].equation = equationText;
    }
    const _savedEquations = get().savedEquations;
    const _isEquationsDirty = Object.keys(_tmpAllEquations).some(
      (rankName) =>
        (_tmpAllEquations[rankName].equation ?? "") !==
        (_savedEquations[rankName] ?? ""),
    );
    set((state) => ({
      allEquations: _tmpAllEquations,
      isEquationsDirty: _isEquationsDirty,
      currentEquation: _tmpAllEquations[rank],
    }));
  },
}));

const makeQuestionSnapshotTree = (
  items: IRankingProjectQuestion[],
  id: number,
  link: string = "parent_question_id",
  layer: number = 1,
  layer_string: string = "",
): IPQuestionTree[] => {
  return items
    .filter((item) => item.parent_question_id === id)
    .sort((a, b) => a.question_order - b.question_order)
    .map((item, index) => ({
      id: item.question_id,
      question: item.description,
      question_variable: item.variable,
      parent_id: item.parent_question_id,
      question_order_text:
        layer_string !== "" ? `${layer_string}.${index + 1}` : `${index + 1}`,
      question_type: item.question_type,
      question_type_id: item.question_type_id,
      depth: layer - 1,
      responses: (item.responses ?? []).map((res) => ({
        id: res.id,
        response: res.description,
        response_variable: res.variable,
        score: res.score ?? 0,
      })),
      children: makeQuestionSnapshotTree(
        items,
        item.question_id,
        "parent_question_id",
        layer + 1,
        layer_string !== "" ? `${layer_string}.${index + 1}` : `${index + 1}`,
      ),
    }));
};

const questionTreeToFlattenQuestionList = (
  items: IPQuestionTree[],
): IPQuestion[] => {
  const flattened: IPQuestion[] = [];

  function flattenNode(node: IPQuestionTree): void {
    const {
      id,
      question,
      question_order_text,
      question_type,
      question_type_id,
      question_variable,
      responses,
      depth,
    } = node;
    flattened.push({
      id,
      question,
      question_order_text,
      question_type,
      question_type_id,
      question_variable,
      responses,
      depth,
    });

    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        flattenNode(child);
      }
    }
  }

  for (const node of items) {
    flattenNode(node);
  }

  return flattened;
};

export const useRankingToolStore = useStore;
